/*
 * MYS-7479: the password policy of the Keycloak "MyServices" realm, mirrored for the forms
 * and APIs that set a password, so a user sees a specific error before Keycloak rejects it.
 *
 * Keycloak stays the authority - its realm policy has to be configured to exactly these rules:
 *   length(14) and maxLength(64) and notUsername and notEmail
 *   and regexPattern(PASSWORD_NO_NUMBER_SEQUENCE_PATTERN) and passwordBlacklist(...)
 * The blacklist can only be checked by Keycloak, see violationFromKeycloakError.
 *
 * 14 characters is the PEAK (Peaksolution) minimum.
 */

export const PASSWORD_MIN_LENGTH = 14
export const PASSWORD_MAX_LENGTH = 64

// ascending or descending runs of three digits ("123", "987"); full-match regex for the
// Keycloak regexPattern policy, which a password has to match
export const PASSWORD_NO_NUMBER_SEQUENCE_PATTERN = '^(?!.*(?:012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|210)).*$'

export enum PasswordPolicyViolation {
	TOO_SHORT = 'tooShort',
	TOO_LONG = 'tooLong',
	NUMBER_SEQUENCE = 'numberSequence',
	EQUALS_EMAIL = 'equalsEmail',
	COMMON_PASSWORD = 'commonPassword',
}

const noNumberSequence = new RegExp(PASSWORD_NO_NUMBER_SEQUENCE_PATTERN)

export function hasNumberSequence(password: string): boolean {
	return !noNumberSequence.test(password)
}

/** Every rule the password breaks, in the order a form should show them. Empty when it is fine. */
export function passwordPolicyViolations(password: string, email?: string): PasswordPolicyViolation[] {
	const violations: PasswordPolicyViolation[] = []
	if (password.length < PASSWORD_MIN_LENGTH) violations.push(PasswordPolicyViolation.TOO_SHORT)
	if (password.length > PASSWORD_MAX_LENGTH) violations.push(PasswordPolicyViolation.TOO_LONG)
	if (hasNumberSequence(password)) violations.push(PasswordPolicyViolation.NUMBER_SEQUENCE)
	// Keycloak's notUsername/notEmail compare case-insensitively; in MYS the username is the email
	if (email && password.toLowerCase() === email.trim().toLowerCase()) violations.push(PasswordPolicyViolation.EQUALS_EMAIL)
	return violations
}

/**
 * For an account whose password nobody types (the user sets their own via the recovery mail):
 * draws until a value passes the policy. The caller supplies the random source, because this
 * module also runs in the browser. Random hex or UUIDs contain "123"-like runs every few tries.
 */
export function compliantRandomPassword(draw: () => string): string {
	let password: string
	do password = draw()
	while (passwordPolicyViolations(password).length)
	return password
}

const KEYCLOAK_POLICY_ERRORS: Record<string, PasswordPolicyViolation> = {
	invalidPasswordMinLengthMessage: PasswordPolicyViolation.TOO_SHORT,
	invalidPasswordMaxLengthMessage: PasswordPolicyViolation.TOO_LONG,
	invalidPasswordRegexPatternMessage: PasswordPolicyViolation.NUMBER_SEQUENCE,
	invalidPasswordNotUsernameMessage: PasswordPolicyViolation.EQUALS_EMAIL,
	invalidPasswordNotEmailMessage: PasswordPolicyViolation.EQUALS_EMAIL,
	invalidPasswordBlacklistedMessage: PasswordPolicyViolation.COMMON_PASSWORD,
}

/**
 * Maps the error body of a rejected Keycloak admin API password call to the violated rule.
 * Keycloak puts the message key in `error` (or `errorMessage`, depending on the endpoint).
 */
export function violationFromKeycloakError(responseData: unknown): PasswordPolicyViolation | undefined {
	if (!responseData || typeof responseData !== 'object') return undefined
	const data = responseData as { error?: unknown, errorMessage?: unknown }
	const key = typeof data.error === 'string' ? data.error : data.errorMessage
	return typeof key === 'string' ? KEYCLOAK_POLICY_ERRORS[key] : undefined
}

export class PasswordPolicyError extends Error {
	constructor(readonly violations: PasswordPolicyViolation[]) {
		super(`password violates the password policy: ${ violations.join(', ') }`)
		this.name = 'PasswordPolicyError'
	}
}

export function assertPasswordPolicy(password: string, email?: string): void {
	const violations = passwordPolicyViolations(password ?? '', email)
	if (violations.length) throw new PasswordPolicyError(violations)
}
